/**
 * Decoration pass: trees, plants, underwater flora, landscape features
 * (ice spikes, icebergs, hoodoos, geodes, lava lakes, springs, fallen logs)
 * and cave-biome dressing (dripstone, lush moss and vines, sculk).
 *
 * Anything wider than one column is rooted on a deterministic origin and may
 * be rooted in a neighbouring chunk; origins within MARGIN of the chunk are
 * replayed here so both sides of a border build the same feature.
 */

import { CHUNK_SIZE, SEA_LEVEL, WORLD_HEIGHT } from '../constants';
import {
  AIR, AMETHYST_ID, AZALEA_ID, BAMBOO_ID, BLUE_ICE_ID, CALCITE_ID, CAVE_VINES_ID, CLAY_ID, COARSE_DIRT_ID,
  CORAL_IDS, DEEPSLATE_ID, DIRT_ID, DRIPSTONE_ID, FALLEN_LOG_IDS, FLOWERING_AZALEA_ID, GRASS_ID,
  ICE_ID, IS_OPAQUE, IS_SOLID, KELP_ID, LAVA_ID, LEAF_LITTER_ID, LILY_PAD_ID, MELON_ID, MOSS_CARPET_ID,
  MOSS_ID, MUD_ID, MYCELIUM_ID, PACKED_ICE_ID, PINK_PETALS_ID, PODZOL_ID, POINTED_DRIPSTONE_DOWN_ID,
  POINTED_DRIPSTONE_UP_ID, POWDER_SNOW_ID, PUMPKIN_ID, RED_SAND_ID, RENDER_SHAPE, RenderShape, SAND_ID,
  SCULK_ID, SEAGRASS_ID, SMOOTH_BASALT_ID, SNOW_BLOCK_ID, SNOW_ID, SNOW_LAYER_ID, STONE_ID, SUGAR_CANE_ID,
  TALL_GRASS_ID, TERRACOTTA_ID, TERRACOTTA_IDS, WATER_ID, WILDFLOWERS_ID, BROWN_MUSHROOM_ID,
  RED_MUSHROOM_ID, DEAD_BUSH_ID, CACTUS_ID,
} from '../blocks';
import { hash2, hash3, mulberry32, type Simplex } from '../noise';
import { Biome, BIOME_LIST, DEEP_DARK_EROSION, DRIPSTONE_CONT, LUSH_HUMID, pickCaveBiome, Tree } from './biomes';
import { type GenCtx, getBlock, MARGIN, pcol, PSIZE, setBlock, vy } from './ctx';
import { growTree } from './trees';
import { sculkShriekers } from './sculkShriekers';

const seedOf = (h: number): number => Math.floor(h * 4294967296);

const TWO_BY_TWO = new Set<Tree>([Tree.MegaSpruce, Tree.MegaPine, Tree.MegaJungle, Tree.DarkOak, Tree.PaleOak]);
const WET_ROOTED = new Set<Tree>([Tree.SwampOak, Tree.Mangrove]);
const isBadlands = (b: number): boolean => b === Biome.Badlands || b === Biome.WoodedBadlands || b === Biome.ErodedBadlands;

/** Blocks ordinary plants grow on. */
function isSoil(id: number): boolean {
  return id === GRASS_ID || id === DIRT_ID || id === PODZOL_ID || id === COARSE_DIRT_ID || id === SNOW_ID ||
    id === MUD_ID || id === MOSS_ID;
}

// --- trees -------------------------------------------------------------------

/** Tree species rooted at this column, or -1. Pure in (seed, column). */
function treeAt(ctx: GenCtx, wx: number, wz: number): number {
  const pi = pcol(ctx, wx, wz);
  const biome = ctx.biome[pi];
  const def = BIOME_LIST[biome];
  if (def.trees <= 0) return -1;
  const salt = ctx.seed ^ 0x51ed;
  const v = hash2(wx, wz, salt);
  if (v >= def.trees / 256) return -1;
  // spacing: only the lowest hash in its 5x5 neighbourhood grows
  for (let dz = -2; dz <= 2; dz++) {
    for (let dx = -2; dx <= 2; dx++) {
      if ((dx !== 0 || dz !== 0) && hash2(wx + dx, wz + dz, salt) < v) return -1;
    }
  }
  let total = 0;
  for (const k of def.treeKinds) total += k[1];
  let pick = hash2(wx, wz, ctx.seed ^ 0xb1ac) * total;
  let kind: Tree = def.treeKinds[0][0];
  for (const k of def.treeKinds) {
    if (pick < k[1]) { kind = k[0]; break; }
    pick -= k[1];
  }
  const top = ctx.top[pi];
  if (top > WORLD_HEIGHT - 40) return -1;
  if (WET_ROOTED.has(kind)) {
    if (top < SEA_LEVEL - 2) return -1;
  } else if (top < SEA_LEVEL) return -1;
  if (biome === Biome.WoodedBadlands && top <= SEA_LEVEL + 20) return -1;
  return kind;
}

function placeTrees(ctx: GenCtx): void {
  const R = MARGIN - 1;
  for (let wz = ctx.oz - R; wz < ctx.oz + CHUNK_SIZE + R; wz++) {
    for (let wx = ctx.ox - R; wx < ctx.ox + CHUNK_SIZE + R; wx++) {
      const kind = treeAt(ctx, wx, wz);
      if (kind < 0) continue;
      const pi = pcol(ctx, wx, wz);
      let g = ctx.top[pi];
      if (TWO_BY_TWO.has(kind as Tree)) {
        // a 2x2 trunk stands on the highest of its four columns
        g = Math.max(g, ctx.top[pi + 1], ctx.top[pi + PSIZE], ctx.top[pi + PSIZE + 1]);
      }
      growTree(ctx, kind as Tree, wx, g, wz, mulberry32(seedOf(hash2(wx, wz, ctx.seed ^ 0x7ee))));
    }
  }
}

// --- landscape features ------------------------------------------------------

/**
 * Visit one deterministic origin per `cell`-sized grid square whose feature
 * (radius <= MARGIN) could reach this chunk.
 */
function eachOrigin(
  ctx: GenCtx, cell: number, salt: number, chance: number,
  fn: (wx: number, wz: number, rnd: () => number) => void
): void {
  const R = MARGIN - 1;
  const gx0 = Math.floor((ctx.ox - R) / cell), gx1 = Math.floor((ctx.ox + CHUNK_SIZE - 1 + R) / cell);
  const gz0 = Math.floor((ctx.oz - R) / cell), gz1 = Math.floor((ctx.oz + CHUNK_SIZE - 1 + R) / cell);
  for (let gz = gz0; gz <= gz1; gz++) {
    for (let gx = gx0; gx <= gx1; gx++) {
      const rnd = mulberry32(seedOf(hash2(gx, gz, ctx.seed ^ salt)));
      if (rnd() >= chance) continue;
      const wx = gx * cell + ((rnd() * cell) | 0);
      const wz = gz * cell + ((rnd() * cell) | 0);
      if (wx < ctx.ox - R || wx >= ctx.ox + CHUNK_SIZE + R || wz < ctx.oz - R || wz >= ctx.oz + CHUNK_SIZE + R) continue;
      fn(wx, wz, rnd);
    }
  }
}

function iceSpikes(ctx: GenCtx): void {
  eachOrigin(ctx, 9, 0x1ce, 0.75, (wx, wz, rnd) => {
    const pi = pcol(ctx, wx, wz);
    if (ctx.biome[pi] !== Biome.IceSpikes) return;
    const g = ctx.top[pi];
    if (g < SEA_LEVEL) return;
    const tall = rnd() < 0.1;
    const h = tall ? 18 + ((rnd() * 16) | 0) : 5 + ((rnd() * 9) | 0);
    const r0 = tall ? 2.2 : 1.4 + rnd() * 1.6;
    for (let y = -3; y <= h; y++) {
      const r = r0 * (1 - Math.max(0, y) / (h + 1));
      const ri = Math.ceil(r);
      for (let dz = -ri; dz <= ri; dz++) {
        for (let dx = -ri; dx <= ri; dx++) {
          if (dx * dx + dz * dz <= r * r + 0.3) setBlock(ctx, wx + dx, g + y, wz + dz, PACKED_ICE_ID);
        }
      }
    }
  });
}

function icebergs(ctx: GenCtx): void {
  eachOrigin(ctx, 26, 0x1ceb, 0.45, (wx, wz, rnd) => {
    const b = ctx.biome[pcol(ctx, wx, wz)];
    if (b !== Biome.FrozenOcean && b !== Biome.DeepFrozenOcean) return;
    const rx = 3 + rnd() * 3.5, rz = 3 + rnd() * 3.5;
    const up = 3 + ((rnd() * 7) | 0), down = 4 + ((rnd() * 5) | 0);
    const blue = rnd() < 0.15;
    for (let dy = -down; dy <= up; dy++) {
      const k = dy >= 0 ? 1 - dy / (up + 1) : 1 - (-dy / (down + 1)) * 0.6;
      for (let dz = -7; dz <= 7; dz++) {
        for (let dx = -7; dx <= 7; dx++) {
          const d = (dx * dx) / (rx * rx * k * k) + (dz * dz) / (rz * rz * k * k);
          if (d > 1) continue;
          const y = SEA_LEVEL + dy;
          const c = getBlock(ctx, wx + dx, y, wz + dz);
          if (c !== AIR && c !== WATER_ID && c !== ICE_ID) continue;
          setBlock(ctx, wx + dx, y, wz + dz, dy >= up - 1 && d < 0.5 ? SNOW_BLOCK_ID : blue && d < 0.25 ? BLUE_ICE_ID : PACKED_ICE_ID);
        }
      }
    }
  });
}

const HOODOO_BANDS = [
  TERRACOTTA_ID, TERRACOTTA_IDS.orange, TERRACOTTA_ID, TERRACOTTA_IDS.yellow, TERRACOTTA_ID,
  TERRACOTTA_IDS.red, TERRACOTTA_IDS.orange, TERRACOTTA_IDS.white, TERRACOTTA_ID,
  TERRACOTTA_IDS.brown, TERRACOTTA_IDS.light_gray, TERRACOTTA_IDS.orange,
];

/** Eroded badlands: clusters of banded terracotta spires. */
function hoodoos(ctx: GenCtx): void {
  eachOrigin(ctx, 7, 0x400d, 0.6, (wx, wz, rnd) => {
    const pi = pcol(ctx, wx, wz);
    if (ctx.biome[pi] !== Biome.ErodedBadlands) return;
    const g = ctx.top[pi];
    if (g < SEA_LEVEL) return;
    const h = 5 + ((rnd() * 14) | 0);
    const r0 = 1 + rnd() * 1.6;
    for (let y = -2; y <= h; y++) {
      const r = y > h - 3 ? r0 * 0.6 : r0;
      const ri = Math.ceil(r);
      for (let dz = -ri; dz <= ri; dz++) {
        for (let dx = -ri; dx <= ri; dx++) {
          if (dx * dx + dz * dz > r * r + 0.4) continue;
          const yy = g + y;
          setBlock(ctx, wx + dx, yy, wz + dz, HOODOO_BANDS[yy % HOODOO_BANDS.length]);
        }
      }
    }
  });
}

/** Amethyst geodes: smooth basalt, calcite and amethyst shells around a hollow. */
function geodes(ctx: GenCtx): void {
  eachOrigin(ctx, 56, 0x6e0d, 0.5, (wx, wz, rnd) => {
    const pi = pcol(ctx, wx, wz);
    const y = vy(-58) + 6 + ((rnd() * (vy(30) - vy(-58) - 6)) | 0);
    if (y > ctx.top[pi] - 10) return;
    const sx = 0.85 + rnd() * 0.3, sz = 0.85 + rnd() * 0.3;
    for (let dy = -6; dy <= 6; dy++) {
      for (let dz = -6; dz <= 6; dz++) {
        for (let dx = -6; dx <= 6; dx++) {
          const d = Math.sqrt(dx * dx * sx + dy * dy + dz * dz * sz) + (hash3(wx + dx, y + dy, wz + dz, ctx.seed) - 0.5) * 0.6;
          if (d > 5.6) continue;
          setBlock(ctx, wx + dx, y + dy, wz + dz, d < 2.6 ? AIR : d < 3.6 ? AMETHYST_ID : d < 4.6 ? CALCITE_ID : SMOOTH_BASALT_ID);
        }
      }
    }
  });
}

/** Lava lakes: rare shallow pools on the surface, more common ones underground. */
function lavaLakes(ctx: GenCtx): void {
  const lake = (wx: number, y: number, wz: number, rnd: () => number, size: number): void => {
    const rx = size + rnd() * size, rz = size + rnd() * size;
    const deep = size < 3 ? 2 : 3;
    for (let dy = -deep; dy <= 3; dy++) {
      const k = 1 - Math.abs(dy) / 4;
      for (let dz = -6; dz <= 6; dz++) {
        for (let dx = -6; dx <= 6; dx++) {
          if ((dx * dx) / (rx * rx * k) + (dz * dz) / (rz * rz * k) > 1) continue;
          setBlock(ctx, wx + dx, y + dy, wz + dz, dy <= 0 ? LAVA_ID : AIR);
        }
      }
    }
  };
  // vanilla: one surface lake per ~200 chunks, and they are small
  eachOrigin(ctx, 226, 0x1a7a, 0.5, (wx, wz, rnd) => {
    const pi = pcol(ctx, wx, wz);
    const g = ctx.top[pi];
    const b = ctx.biome[pi];
    if (g < SEA_LEVEL + 2 || BIOME_LIST[b].frozen) return;
    // only on level ground, or the rim would spill
    for (const o of [-4, 4, -4 * PSIZE, 4 * PSIZE]) if (Math.abs(ctx.top[pi + o] - g) > 1) return;
    lake(wx, g, wz, rnd, 2);
  });
  eachOrigin(ctx, 40, 0x1a7b, 0.5, (wx, wz, rnd) => {
    const y = 8 + ((rnd() * 28) | 0);
    if (y > ctx.top[pcol(ctx, wx, wz)] - 14) return;
    lake(wx, y, wz, rnd, 3);
  });
}

/** Fallen logs on forest floors (1.21.5). */
function fallenLogs(ctx: GenCtx): void {
  eachOrigin(ctx, 14, 0xfa11, 0.5, (wx, wz, rnd) => {
    const pi = pcol(ctx, wx, wz);
    const b = ctx.biome[pi];
    let species: number;
    if (b === Biome.Forest || b === Biome.FlowerForest || b === Biome.DarkForest || b === Biome.Plains) species = 0;
    else if (b === Biome.BirchForest || b === Biome.OldGrowthBirchForest) species = 1;
    else if (b === Biome.Taiga || b === Biome.OldGrowthPineTaiga || b === Biome.OldGrowthSpruceTaiga || b === Biome.SnowyTaiga) species = 2;
    else return;
    if (b === Biome.Plains && rnd() > 0.1) return;
    const g = ctx.top[pi];
    if (g < SEA_LEVEL) return;
    const axis = rnd() < 0.5 ? 0 : 1;
    const len = 3 + ((rnd() * 4) | 0);
    const step = axis === 0 ? 1 : PSIZE;
    for (let i = 1; i < len; i++) if (ctx.top[pi + i * step] !== g) return; // needs level ground
    for (let i = 0; i < len; i++) {
      const x = wx + (axis === 0 ? i : 0), z = wz + (axis === 1 ? i : 0);
      if (getBlock(ctx, x, g + 1, z) === AIR && isSoil(getBlock(ctx, x, g, z))) {
        setBlock(ctx, x, g + 1, z, FALLEN_LOG_IDS[species][axis]);
        if (rnd() < 0.25 && getBlock(ctx, x, g + 2, z) === AIR) {
          setBlock(ctx, x, g + 2, z, rnd() < 0.5 ? BROWN_MUSHROOM_ID : RED_MUSHROOM_ID);
        }
      }
    }
  });
}

/** Springs: a source block in a cave wall with a still column falling from it. */
function springs(ctx: GenCtx): void {
  const out = ctx.out;
  const rnd = mulberry32(seedOf(hash2(ctx.cx, ctx.cz, ctx.seed ^ 0x5b21)));
  for (let n = 0; n < 14; n++) {
    const x = 1 + ((rnd() * 14) | 0), z = 1 + ((rnd() * 14) | 0);
    const lava = n >= 10;
    const y = 4 + ((rnd() * (lava ? 30 : 80)) | 0);
    const i = x + z * CHUNK_SIZE + y * 256;
    if (out[i] !== STONE_ID && out[i] !== DEEPSLATE_ID) continue;
    if (IS_OPAQUE[out[i + 256]] !== 1 || IS_OPAQUE[out[i - 256]] !== 1) continue;
    let open = 0, dir = 0;
    for (const d of [1, -1, CHUNK_SIZE, -CHUNK_SIZE]) {
      if (out[i + d] === AIR) { open++; dir = d; } else if (IS_OPAQUE[out[i + d]] !== 1) open = 9;
    }
    if (open !== 1) continue;
    const fluid = lava ? LAVA_ID : WATER_ID;
    out[i] = fluid;
    for (let j = i + dir; j >= 256 && out[j] === AIR; j -= 256) out[j] = fluid;
  }
}

// --- plants ------------------------------------------------------------------

function underwater(ctx: GenCtx, x: number, z: number, wx: number, wz: number, top: number, biome: number, noise: Simplex): void {
  const out = ctx.out;
  const base = x + z * CHUNK_SIZE;
  const floor = out[base + top * 256];
  if (IS_SOLID[floor] !== 1 || out[base + (top + 1) * 256] !== WATER_ID) return;
  const depth = SEA_LEVEL - top;
  const r = hash2(wx, wz, ctx.seed ^ 0x5ea);
  const def = BIOME_LIST[biome];

  if (biome === Biome.WarmOcean && depth >= 3) {
    // coral reef: mounds of coral blocks, one colour per small patch
    const reef = noise.noise2(wx * 0.022 + 200, wz * 0.022 - 200);
    if (reef > 0.15) {
      const h = Math.min(depth - 2, Math.floor((noise.noise2(wx * 0.17, wz * 0.17) + 0.15) * 5));
      const colour = CORAL_IDS[(hash2(wx >> 2, wz >> 2, ctx.seed ^ 0xc0a1) * CORAL_IDS.length) | 0];
      for (let y = 1; y <= h; y++) out[base + (top + y) * 256] = colour;
      if (h > 0) return;
    }
  }
  if (def.frozen) return;

  const ocean = biome <= Biome.DeepFrozenOcean;
  if (ocean && biome !== Biome.WarmOcean && depth >= 4) {
    // kelp forests: dense stands in noise-shaped patches
    const patch = noise.noise2(wx * 0.013 - 300, wz * 0.013 + 300);
    if (patch > 0.1 && r < 0.3) {
      const h = Math.min(depth - 1, 2 + ((hash2(wx, wz, ctx.seed ^ 0x4e1) * (depth - 1)) | 0));
      for (let y = 1; y <= h; y++) out[base + (top + y) * 256] = KELP_ID;
      return;
    }
  }
  const grass = biome === Biome.WarmOcean ? 0.3 : biome === Biome.LukewarmOcean || biome === Biome.DeepLukewarmOcean ? 0.22
    : biome === Biome.Swamp || biome === Biome.MangroveSwamp ? 0.25 : biome === Biome.River ? 0.18 : ocean ? 0.1 : 0.06;
  if (depth >= 2 && r > 0.4 && r < 0.4 + grass) out[base + (top + 1) * 256] = SEAGRASS_ID;
}

/**
 * Sugar cane, mirroring vanilla `patch_sugar_cane`: rarity 1/6 per chunk, one
 * patch of 20 tries spread x/z ±4 around a chunk-local origin, height 2–4
 * biased to bottom (2: 61%, 3: 28%, 4: 11%). Each try must land on
 * grass/dirt/sand at sea level with water adjacent at ground level (the JSON
 * predicate's [±1, -1, 0] fluid check) in a non-frozen biome.
 */
function sugarCane(ctx: GenCtx): void {
  if (hash2(ctx.ox, ctx.oz, ctx.seed ^ 0xca4e) >= 1 / 6) return;
  const rng = mulberry32(seedOf(hash2(ctx.ox, ctx.oz, ctx.seed ^ 0x5c4c)));
  const ox = ctx.ox + ((rng() * CHUNK_SIZE) | 0);
  const oz = ctx.oz + ((rng() * CHUNK_SIZE) | 0);
  const out = ctx.out;
  for (let t = 0; t < 20; t++) {
    const wx = ox + ((rng() * 9 - 4) | 0);
    const wz = oz + ((rng() * 9 - 4) | 0);
    const x = wx - ctx.ox, z = wz - ctx.oz;
    if (x < 0 || x >= CHUNK_SIZE || z < 0 || z >= CHUNK_SIZE) continue; // writes stay in this chunk
    const pi = pcol(ctx, wx, wz);
    if (ctx.top[pi] !== SEA_LEVEL) continue;
    if (BIOME_LIST[ctx.biome[pi]].frozen) continue;
    const ground = out[x + z * CHUNK_SIZE + SEA_LEVEL * 256];
    if (ground !== GRASS_ID && ground !== SAND_ID && ground !== DIRT_ID) continue;
    // water adjacent at ground level (a lower neighbouring top means sea water)
    const wet = ctx.top[pi - 1] < SEA_LEVEL || ctx.top[pi + 1] < SEA_LEVEL ||
      ctx.top[pi - PSIZE] < SEA_LEVEL || ctx.top[pi + PSIZE] < SEA_LEVEL;
    if (!wet) continue;
    const v = rng();
    const h = v < 0.61 ? 2 : v < 0.89 ? 3 : 4;
    const base = x + z * CHUNK_SIZE + (SEA_LEVEL + 1) * 256;
    if (out[base] !== AIR) continue;
    for (let y = 0; y < h; y++) out[base + y * 256] = SUGAR_CANE_ID;
  }
}

function plants(ctx: GenCtx, noise: Simplex): void {
  const out = ctx.out;
  for (let z = 0; z < CHUNK_SIZE; z++) {
    for (let x = 0; x < CHUNK_SIZE; x++) {
      const wx = ctx.ox + x, wz = ctx.oz + z;
      const pi = x + MARGIN + (z + MARGIN) * PSIZE;
      const top = ctx.top[pi];
      const biome = ctx.biome[pi];
      const def = BIOME_LIST[biome];
      const base = x + z * CHUNK_SIZE;

      if (top < SEA_LEVEL) {
        underwater(ctx, x, z, wx, wz, top, biome, noise);
        // lily pads float on swamp water
        if ((biome === Biome.Swamp || biome === Biome.MangroveSwamp) && top >= SEA_LEVEL - 3 &&
          out[base + SEA_LEVEL * 256] === WATER_ID && out[base + (SEA_LEVEL + 1) * 256] === AIR &&
          hash2(wx, wz, ctx.seed ^ 0x1117) < 0.08) out[base + (SEA_LEVEL + 1) * 256] = LILY_PAD_ID;
        continue;
      }
      const gi = base + top * 256;
      const ai = gi + 256;
      const ground = out[gi];
      if (out[ai] !== AIR) continue;
      const r = hash2(wx, wz, ctx.seed ^ 0xf10a);

      if (isSoil(ground) || ground === MYCELIUM_ID) {
        let acc = 0;
        for (const [id, chance] of def.flora) {
          acc += chance;
          if (r >= acc) continue;
          if (ground === MYCELIUM_ID && id !== RED_MUSHROOM_ID && id !== BROWN_MUSHROOM_ID) break;
          if (id === BAMBOO_ID) {
            const h = 5 + ((hash2(wx, wz, ctx.seed ^ 0xba3) * 9) | 0);
            for (let y = 0; y < h && out[ai + y * 256] === AIR; y++) out[ai + y * 256] = BAMBOO_ID;
          } else if (id === PUMPKIN_ID || id === MELON_ID) {
            // patches: a few fruit around the origin column
            out[ai] = id;
          } else out[ai] = id;
          break;
        }
        if (out[ai] !== AIR) continue;
        // ground cover (drawn from a second hash so it layers under the plants' gaps)
        const c = hash2(wx, wz, ctx.seed ^ 0xc0fe);
        if (ground === GRASS_ID) {
          if (biome === Biome.CherryGrove && c < 0.35) out[ai] = PINK_PETALS_ID;
          else if ((biome === Biome.Forest || biome === Biome.DarkForest || biome === Biome.WoodedBadlands) && c < 0.07) out[ai] = LEAF_LITTER_ID;
          else if ((biome === Biome.BirchForest || biome === Biome.OldGrowthBirchForest || biome === Biome.Meadow) && c < 0.06) out[ai] = WILDFLOWERS_ID;
        }
      } else if (ground === SAND_ID || ground === RED_SAND_ID || (isBadlands(biome) && ground !== STONE_ID)) {
        let acc = 0;
        for (const [id, chance] of def.flora) {
          acc += chance;
          if (r < acc) { if (id === DEAD_BUSH_ID || id === CACTUS_ID) out[ai] = id; break; }
        }
      }
    }
  }
}

/** Snow layers on everything exposed in frozen biomes (ground, leaves, logs). */
function snowCover(ctx: GenCtx): void {
  const out = ctx.out;
  for (let z = 0; z < CHUNK_SIZE; z++) {
    for (let x = 0; x < CHUNK_SIZE; x++) {
      const pi = x + MARGIN + (z + MARGIN) * PSIZE;
      if (!BIOME_LIST[ctx.biome[pi]].frozen) continue;
      const base = x + z * CHUNK_SIZE;
      const top = ctx.top[pi];
      for (let y = Math.min(WORLD_HEIGHT - 2, top + 38); y >= top; y--) {
        const id = out[base + y * 256];
        if (id === AIR) continue;
        if (
          RENDER_SHAPE[id] === RenderShape.Cube && id !== WATER_ID && id !== ICE_ID && id !== SNOW_ID &&
          id !== SNOW_BLOCK_ID && id !== POWDER_SNOW_ID && id !== PACKED_ICE_ID && id !== LAVA_ID
        ) out[base + (y + 1) * 256] = SNOW_LAYER_ID;
        break;
      }
    }
  }
}

// --- cave biomes -------------------------------------------------------------

const DEEP_DARK_Y = vy(-10);

/** Can this block wall in a lush-cave pool? (solid rock, or more of the pool) */
const holdsWater = (id: number): boolean => IS_OPAQUE[id] === 1 || id === WATER_ID;

function caveDecor(ctx: GenCtx, noise: Simplex): void {
  const out = ctx.out;
  for (let z = 0; z < CHUNK_SIZE; z++) {
    for (let x = 0; x < CHUNK_SIZE; x++) {
      const pi = x + MARGIN + (z + MARGIN) * PSIZE;
      const top = ctx.top[pi];
      const humid = ctx.humid[pi], cont = ctx.cont[pi], erosion = ctx.erosion[pi];
      // cheap reject: no cave biome can apply to this column at any depth
      if (humid <= LUSH_HUMID && cont <= DRIPSTONE_CONT && erosion >= DEEP_DARK_EROSION) continue;
      const wx = ctx.ox + x, wz = ctx.oz + z;
      const base = x + z * CHUNK_SIZE;
      const patch = noise.noise2(wx * 0.11 + 500, wz * 0.11 + 500);
      for (let y = 2; y < top - 10; y++) {
        const i = base + y * 256;
        if (out[i] !== AIR) continue;
        const below = out[i - 256], above = out[i + 256];
        const floor = below === STONE_ID || below === DEEPSLATE_ID;
        const ceil = above === STONE_ID || above === DEEPSLATE_ID;
        if (!floor && !ceil) continue;
        const biome = pickCaveBiome(humid, cont, erosion, top - y, y, DEEP_DARK_Y);
        if (biome < 0) continue;
        const r = hash3(wx, y, wz, ctx.seed ^ 0xca7e);
        if (biome === Biome.DripstoneCaves) {
          if (floor && patch > -0.2) {
            out[i - 256] = DRIPSTONE_ID;
            if (r < 0.14) out[i] = POINTED_DRIPSTONE_UP_ID;
          }
          if (ceil && patch > -0.2) {
            out[i + 256] = DRIPSTONE_ID;
            if (out[i] === AIR && r > 0.82) out[i] = POINTED_DRIPSTONE_DOWN_ID;
          }
        } else if (biome === Biome.LushCaves) {
          if (floor && patch > -0.5) {
            if (patch > 0.45) {
              // clay pools: water wherever the basin is closed on every side
              const f = i - 256;
              const pool = x > 0 && x < CHUNK_SIZE - 1 && z > 0 && z < CHUNK_SIZE - 1 && y > 2 &&
                holdsWater(out[f - 1]) && holdsWater(out[f + 1]) && holdsWater(out[f - CHUNK_SIZE]) &&
                holdsWater(out[f + CHUNK_SIZE]) && IS_OPAQUE[out[f - 256]] === 1;
              out[f] = pool ? WATER_ID : CLAY_ID;
              if (pool) out[f - 256] = CLAY_ID;
            } else {
              out[i - 256] = MOSS_ID;
              out[i] = r < 0.04 ? AZALEA_ID : r < 0.06 ? FLOWERING_AZALEA_ID : r < 0.2 ? MOSS_CARPET_ID : r < 0.4 ? TALL_GRASS_ID : AIR;
            }
          }
          if (ceil && patch > -0.5) {
            out[i + 256] = MOSS_ID;
            if (out[i] === AIR && r > 0.72) {
              // glow-berry vines hang a few blocks down, lighting the cave
              const len = 1 + ((hash3(wx, y, wz, ctx.seed ^ 0x61e) * 5) | 0);
              for (let k = 0; k < len && out[i - k * 256] === AIR; k++) out[i - k * 256] = CAVE_VINES_ID;
            }
          }
        } else if (patch > -0.45) {
          if (floor) out[i - 256] = SCULK_ID;
          if (ceil && r < 0.5) out[i + 256] = SCULK_ID;
        }
      }
    }
  }
}

/** Underground features first, then surface features bottom-up (trees before plants before snow). */
export function decorate(ctx: GenCtx, noise: Simplex): void {
  geodes(ctx);
  lavaLakes(ctx);
  springs(ctx);
  caveDecor(ctx, noise);
  sculkShriekers(ctx); // summoning shriekers + a catalyst on some deep-dark sculk floors
  icebergs(ctx);
  iceSpikes(ctx);
  hoodoos(ctx);
  placeTrees(ctx);
  fallenLogs(ctx);
  sugarCane(ctx);
  plants(ctx, noise);
  snowCover(ctx);
}
